import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Target, Trophy, CheckCircle2, ChevronRight, Shield } from 'lucide-react';
import { GlassButton } from '../components/ui/glass-button';
import { ShaderAnimation } from '../components/ui/shader-animation';
import { useNavigate, useLocation } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

import ds200 from '../assets/ds200.png';
import ds200_1 from '../assets/ds200(1).png';
import ds200_2 from '../assets/ds200(2).png';
import ds230 from '../assets/ds230.png';
import ds230_1 from '../assets/ds230(1).png';

// ─── DATA ─────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    icon: CheckCircle2,
    number: '01',
    title: 'ANAOKULU DRONE EĞİTİMİ',
    description: 'En küçük boy mini dronelar ile temel beceri geliştirme eğitimleri verilmektedir. El göz koordinasyonu, dikkat gelişimini desteklerken satranç oynar gibi karşı tarafın hamlesini tahmin ve strateji üretmesini sağlayarak bilişsel gelişime ciddi katkı sağlar. FA1 F9A modellerle kafes içinde güvenli dronelar kullanılır.',
    label: '5-6 YAŞ',
    accent: '#e65100',
    light: 'rgba(230,81,0,0.09)',
  },
  {
    icon: Zap,
    number: '02',
    title: 'İLKOKUL DRONE EĞİTİMİ',
    description: 'Eğitimler temel beceri geliştirme çalışmaları ile başlatılmaktadır. Kumanda kullanımı, batarya değiştirme ve belirlenen rotada uçuş hakimiyeti üzerinde çalışılır. Kullanılan dronelar 110 gr olup karbon fiber kafes içinde tam korumalıdır.',
    label: '7-12 YAŞ',
    accent: '#0f2d5e',
    light: 'rgba(15,45,94,0.09)',
  },
  {
    icon: Target,
    number: '03',
    title: 'ORTAOKUL DRONE EĞİTİMİ',
    description: 'Eğitimler temel beceri geliştirme çalışmaları ile başlar. 40 cm çapından daha büyük güçlü motorlara sahip dronelarla çalışılır. Daha profesyonel düzeyde kumanda ve bataryalar kullanılır.',
    label: '13-18 YAŞ',
    accent: '#e65100',
    light: 'rgba(230,81,0,0.09)',
  },
  {
    icon: Trophy,
    number: '04',
    title: 'LİSE VE YETİŞKİN DRONE EĞİTİMİ',
    description: 'Uzun menzil, hız, maksimum çeviklik kapasitesine sahip dronelar kullanılmaktadır. Her yarışmacının ulusal ve uluslararası turnuva kriterlerine uygun kendine ait drone\'u ile çalışmalar yapılır. Bu kategori için en az 8 kişilik grup açılması gerekmektedir.',
    label: '18 YAŞ VE ÜZERİ',
    accent: '#0f2d5e',
    light: 'rgba(15,45,94,0.09)',
  },
];

const DroneIcon = ({ size = 24, color = "currentColor" }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
    <path d="M12 9l0 -4" />
    <path d="M12 15l0 4" />
    <path d="M9 12l-4 0" />
    <path d="M15 12l4 0" />
    <path d="M5 5l2 2" />
    <path d="M19 19l-2 -2" />
    <path d="M19 5l-2 2" />
    <path d="M5 19l2 -2" />
    <path d="M2 5h6" />
    <path d="M2 19h6" />
    <path d="M16 5h6" />
    <path d="M16 19h6" />
  </svg>
);

const EDUCATION_DATA = [
  {
    id: 'anaokulu',
    title: 'ANASINIFI',
    droneName: 'Anasınıfı Drone Eğitimi',
    description: 'En miniklerimiz için eğlenceli ve güvenli bir başlangıç! Bu aşamada çocuklarımız dronu tanır, parçalarını söküp takarak ince motor becerilerini geliştirir ve drone teknolojisine ilk heyecanlı adımlarını oyunlarla atarlar.',
    features: ['Parça söküp takma etkinlikleri', 'Güvenli tasarımla donanımı tanıma', 'El-göz koordinasyonu geliştirici oyunlar'],
    images: [ds200, ds200_1],
    color: '#e65100'
  },
  {
    id: 'ilkokul',
    title: 'İLKOKUL',
    droneName: 'İlkokul Drone Eğitimi',
    description: 'Çocuklarımızın ilk uçuş deneyimlerini güvenle yaşadığı aşamadır! Bu seviyede temel uçuş dinamikleri kavranır, dronu çembere göre ayarlama ve hedeften geçme gibi eğlenceli pratiklerle yönlendirme becerileri kazanılır.',
    features: ['İlk uçuş deneyimi ve temel pilotaj', 'Çember hedeflerine yönelik hizalama', 'Takım çalışması ve yön duygusu'],
    images: [ds200_2],
    color: '#0f2d5e'
  },
  {
    id: 'ortaokul',
    title: 'ORTAOKUL',
    droneName: 'Ortaokul Drone Eğitimi',
    description: 'Artık hız ve kontrol zamanı! Gençlerimiz bu aşamada dronlarını hedefe doğru hızlandırmayı, rakipleri arasından sıyrılarak çemberden hatasız geçirmeyi ve stratejik takım oyununu öğrenirler.',
    features: ['Hedefe yönelik ivmelenme ve hız kontrolü', 'Hareket halindeyken çemberden geçiş', 'Hızlı manevra ve görev odaklı uçuş'],
    images: [ds230],
    color: '#e65100'
  },
  {
    id: 'lise',
    title: 'LİSE',
    droneName: 'Lise Drone Eğitimi',
    description: 'Ustalık ve strateji dönemi! Bu son aşamada gençlerimiz gerçek bir Drone Soccer maçı heyecanıyla takım rollerini üstlenir; rakip savunmayı aşıp çemberden gol atmayı ve kendi sahasını hava blokajlarıyla korumayı ustalıkla öğrenirler.',
    features: ['Hücum (Forvet) ve savunma (Blok) taktikleri', 'Profesyonel maç stratejileri ve saha okuma', 'Turnuva düzeyinde refleks ve takım koordinasyonu'],
    images: [ds230_1],
    color: '#0f2d5e'
  }
];

const ImageSlider = ({ images, alt, isMobile }: { images: string[], alt: string, isMobile: boolean }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images]);

  if (images.length === 1) {
    return (
      <img
        src={images[0]}
        alt={alt}
        loading="lazy"
        style={{ width: '100%', maxWidth: isMobile ? '280px' : '380px', height: 'auto', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.15))' }}
      />
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: isMobile ? '280px' : '380px', aspectRatio: '1/1', overflow: 'hidden' }}>
      <motion.div
        animate={{ x: `-${currentIndex * 100}%` }}
        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
        style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center' }}
      >
        {images.map((src, idx) => (
          <div key={idx} style={{ minWidth: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={src}
              alt={alt}
              loading="lazy"
              style={{ width: '100%', height: 'auto', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.15))' }}
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// ─── DRONE ORBIT ──────────────────────────────────────────────────────────────

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('drone-orbit-style')) {
  const s = document.createElement('style');
  s.id = 'drone-orbit-style';
  s.textContent = `
    @keyframes droneOrbit {
      from { transform: rotate(0deg) translateX(var(--r)) rotate(0deg); }
      to   { transform: rotate(360deg) translateX(var(--r)) rotate(-360deg); }
    }
    .drone-orbit {
      position: absolute;
      top: 50%; left: 50%;
      margin: -8px 0 0 -8px;
      width: 16px; height: 16px;
      animation: droneOrbit var(--dur, 6s) linear infinite;
      animation-delay: var(--delay, 0s);
    }
  `;
  document.head.appendChild(s);
}

// Inline minimal drone geometry (same as Drone3D)
const MiniDroneGeom = () => {
  const ref = React.useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 1.5;
  });
  return (
    <group ref={ref}>
      <mesh><sphereGeometry args={[0.3, 16, 16]} /><meshStandardMaterial color="#0f2d5e" metalness={0.2} roughness={0.4} /></mesh>
      <mesh><sphereGeometry args={[0.15, 8, 8]} /><meshBasicMaterial color="#4fc3f7" toneMapped={false} /></mesh>
      {[[0.4, 0, 0.4], [-0.4, 0, -0.4], [-0.4, 0, 0.4], [0.4, 0, -0.4]].map((p, i) => (
        <mesh key={i} position={p as any} rotation={[0, i % 2 === 0 ? -Math.PI / 4 : Math.PI / 4, 0]}>
          <boxGeometry args={[0.8, 0.05, 0.1]} /><meshStandardMaterial color="#fff" metalness={0.6} roughness={0.3} />
        </mesh>
      ))}
      {[[0.7, 0.1, 0.7], [-0.7, 0.1, -0.7], [-0.7, 0.1, 0.7], [0.7, 0.1, -0.7]].map((p, i) => (
        <group key={i} position={p as any}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.4, 0.04, 8, 24]} /><meshStandardMaterial color="#fff" metalness={0.6} roughness={0.3} /></mesh>
        </group>
      ))}
      {/* Soccer Ball Cage */}
      <mesh rotation={[0, 0, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
      <mesh rotation={[0, Math.PI / 2, 0]}><torusGeometry args={[1.2, 0.03, 6, 32]} /><meshStandardMaterial color="#ffffff" metalness={0.5} roughness={0} /></mesh>
    </group>
  );
};

const SmallDrone = ({ size = 48 }: { size?: number }) => (
  <div style={{ width: size, height: size, flexShrink: 0 }}>
    <Canvas
      camera={{ position: [0, 1, 3], fov: 55 }}
      dpr={1}
      gl={{ antialias: false, powerPreference: 'high-performance', alpha: true }}
      style={{ background: 'transparent' }}
    >
      <ambientLight intensity={0.9} />
      <directionalLight position={[5, 5, 5]} intensity={2} />
      <Float speed={3} floatIntensity={0.5}>
        <group scale={0.65}><MiniDroneGeom /></group>
      </Float>
    </Canvas>
  </div>
);

// Per-card orbiter configs — each card has different drone count & random coords
const STEP_ORBITERS = [
  // Card 01 — ÖN BAŞVURU: 2 drones
  [
    { r: '152px', dur: '8s', delay: '0s', size: 46, opacity: 0.80 },
    { r: '128px', dur: '13s', delay: '-5s', size: 38, opacity: 0.60 },
  ],
  // Card 02 — TEMEL EĞİTİM: 4 drones
  [
    { r: '138px', dur: '7s', delay: '0s', size: 50, opacity: 0.85 },
    { r: '168px', dur: '10s', delay: '-3s', size: 36, opacity: 0.55 },
    { r: '118px', dur: '14s', delay: '-8s', size: 42, opacity: 0.70 },
    { r: '180px', dur: '11s', delay: '-1.5s', size: 32, opacity: 0.45 },
  ],
  // Card 03 — TAKIM SEÇMELERİ: 1 drone
  [
    { r: '145px', dur: '9s', delay: '-4s', size: 52, opacity: 0.90 },
  ],
  // Card 04 — TURNUVALAR: 3 drones
  [
    { r: '160px', dur: '6s', delay: '0s', size: 44, opacity: 0.80 },
    { r: '122px', dur: '12s', delay: '-6s', size: 40, opacity: 0.65 },
    { r: '175px', dur: '9s', delay: '-2s', size: 34, opacity: 0.50 },
  ],
];

const DroneCard = ({ step, stepIndex, children }: { step: typeof STEPS[0]; stepIndex: number; children: React.ReactNode }) => {
  const orbiters = STEP_ORBITERS[stepIndex] ?? STEP_ORBITERS[0];
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      {orbiters.map((o, i) => (
        <div
          key={i}
          className="drone-orbit"
          style={{
            '--r': o.r,
            '--dur': o.dur,
            '--delay': o.delay,
            opacity: o.opacity,
            pointerEvents: 'none',
          } as React.CSSProperties}
        >
          <SmallDrone size={o.size} />
        </div>
      ))}
      {children}
    </div>
  );
};


// ─── CARD INNER ───────────────────────────────────────────────────────────────

const CardInner = ({ step, isMobile }: { step: typeof STEPS[0]; isMobile?: boolean }) => (
  <div style={{ background: '#fff', borderRadius: 22, border: '1px solid #ececec', boxShadow: '0 4px 28px rgba(0,0,0,0.09)', overflow: 'hidden', maxWidth: isMobile ? 420 : 440, width: '100%' }}>
    <div style={{ height: 4, background: step.accent }} />
    <div style={{ padding: isMobile ? '20px 22px' : '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 12 : 16 }}>
        <div style={{ width: isMobile ? 48 : 52, height: isMobile ? 48 : 52, borderRadius: 14, background: step.light, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <step.icon style={{ width: isMobile ? 22 : 24, height: isMobile ? 22 : 24, color: step.accent, strokeWidth: 2 }} />
        </div>
      </div>
      <p style={{ fontSize: '10px', fontWeight: 800, color: step.accent, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: isMobile ? 6 : 8 }}>{(step as any).label || `ADIM ${step.number}`}</p>
      <h3 style={{ fontSize: isMobile ? '1.15rem' : '1.2rem', fontWeight: 900, color: '#0f2d5e', marginBottom: isMobile ? 8 : 10, lineHeight: 1.2, fontFamily: 'var(--font-headline)' }}>{step.title}</h3>
      <p style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', color: '#0f2d5e', lineHeight: 1.7, opacity: 0.8, fontWeight: 500 }}>{step.description}</p>
    </div>
  </div>
);

// ─── ZIGZAG STEPS ─────────────────────────────────────────────────────────────

const ZigzagSteps = ({ isMobile }: { isMobile: boolean }) => (
  <div style={{ position: 'relative' }}>
    {STEPS.map((step, idx) => {
      const isLeft = idx % 2 === 0;
      return (
        <div
          key={idx}
          style={{
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: isMobile ? undefined : '1fr 72px 1fr',
            alignItems: 'center',
            marginBottom: idx === STEPS.length - 1 ? 0 : isMobile ? 16 : 0,
          }}
        >
          {isMobile ? (
            // Mobile: full-width stacked
            <motion.div
              style={{ width: '100%' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: idx * 0.08 }}
            >
              <DroneCard step={step} stepIndex={idx}>
                <CardInner step={step} isMobile={true} />
              </DroneCard>
            </motion.div>
          ) : (
            <>
              {/* Left column */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: 36, paddingTop: 52, paddingBottom: 52 }}>
                {isLeft && (
                  <motion.div
                    initial={{ opacity: 0, x: -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                  >
                    <DroneCard step={step} stepIndex={idx}>
                      <CardInner step={step} />
                    </DroneCard>
                  </motion.div>
                )}
              </div>

              {/* Center: line + dot */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' }}>
                {/* Top segment */}
                <div style={{ flex: 1, width: 1, background: idx === 0 ? 'transparent' : '#e5e7eb' }} />
                {/* Dot */}
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: step.accent, boxShadow: `0 0 10px ${step.accent}70`, flexShrink: 0, zIndex: 2 }} />
                {/* Bottom segment */}
                <div style={{ flex: 1, width: 1, background: idx === STEPS.length - 1 ? 'transparent' : '#e5e7eb' }} />
              </div>

              {/* Right column */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: 36, paddingTop: 52, paddingBottom: 52 }}>
                {!isLeft && (
                  <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                  >
                    <DroneCard step={step} stepIndex={idx}>
                      <CardInner step={step} />
                    </DroneCard>
                  </motion.div>
                )}
              </div>
            </>
          )}
        </div>
      );
    })}
  </div>
);

const EducationSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section id="kademeli-dronelar" className="relative w-full py-16 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-16 relative z-10">

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{ color: '#e65100', fontSize: isMobile ? '0.65rem' : '0.75rem', fontWeight: 900, letterSpacing: '0.4em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}
          >
            EĞİTİM MODELLERİMİZ
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="font-headline font-black uppercase tracking-tighter"
            style={{ fontSize: isMobile ? '1.8rem' : '3.5rem', color: '#0f2d5e', lineHeight: 1.1, marginBottom: '20px' }}
          >
            KADEMELERLE <span style={{ color: '#e65100' }}>DRONE</span> EĞİTİMLERİMİZ
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            style={{ maxWidth: '600px', margin: '0 auto', color: '#4b5563', fontSize: isMobile ? '0.95rem' : '1.1rem', fontWeight: 500, lineHeight: 1.6 }}
          >
            Yaş gruplarına özel olarak optimize edilmiş, güvenli ve performans odaklı eğitim setlerimizle geleceğin insansız hava aracı pilotlarını yetiştiriyoruz.
          </motion.p>
        </div>

        {/* Tab Bar */}
        <div style={{
          display: 'flex',
          justifyContent: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '12px',
          marginBottom: isMobile ? '32px' : '48px',
          overflowX: 'auto',
          paddingBottom: '12px',
          paddingLeft: isMobile ? '4px' : '0',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} className="hide-scrollbar">
          {EDUCATION_DATA.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(idx)}
              style={{
                padding: isMobile ? '12px 20px' : '14px 28px',
                borderRadius: '14px',
                background: activeTab === idx ? '#0f2d5e' : 'rgba(15, 45, 94, 0.04)',
                color: activeTab === idx ? '#fff' : '#0f2d5e',
                border: 'none',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                boxShadow: activeTab === idx ? '0 10px 25px rgba(15, 45, 94, 0.2)' : 'none',
                flexShrink: 0
              }}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div style={{
          background: 'rgba(15, 45, 94, 0.02)',
          borderRadius: isMobile ? '30px' : '40px',
          border: '1px solid rgba(15, 45, 94, 0.05)',
          padding: isMobile ? '24px' : '64px',
          minHeight: isMobile ? 'auto' : '400px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '40px' : '48px', alignItems: 'center' }}
            >
              {/* Left: Image Side */}
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: `radial-gradient(circle, ${EDUCATION_DATA[activeTab].color}15 0%, transparent 70%)`,
                  borderRadius: '50%',
                  filter: 'blur(30px)'
                }} />
                <div
                  style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'center' }}
                >
                  <ImageSlider images={EDUCATION_DATA[activeTab].images} alt={EDUCATION_DATA[activeTab].droneName} isMobile={isMobile} />
                </div>

                {/* Drone Info Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: isMobile ? '-10px' : '-20px',
                  right: isMobile ? '10px' : '-20px',
                  background: '#fff',
                  padding: isMobile ? '12px 16px' : '16px 24px',
                  borderRadius: '16px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(0,0,0,0.05)',
                  zIndex: 3
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                    <div style={{ color: EDUCATION_DATA[activeTab].color }}>
                      <DroneIcon size={isMobile ? 20 : 24} />
                    </div>
                    <div>
                      <div style={{ fontSize: isMobile ? '0.55rem' : '0.65rem', fontWeight: 900, color: '#9ca3af', letterSpacing: '0.1em' }}>MODEL</div>
                      <div style={{ fontSize: isMobile ? '0.85rem' : '1rem', fontWeight: 900, color: '#0f2d5e' }}>{EDUCATION_DATA[activeTab].droneName}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Text Side */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
                <h3 className="font-headline" style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 900, color: '#0f2d5e', lineHeight: 1.2 }}>
                  {EDUCATION_DATA[activeTab].droneName}
                </h3>
                <p style={{ fontSize: isMobile ? '0.9rem' : '1rem', color: '#4b5563', lineHeight: 1.6, fontWeight: 500 }}>
                  {EDUCATION_DATA[activeTab].description}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  {EDUCATION_DATA[activeTab].features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                        background: `${EDUCATION_DATA[activeTab].color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: EDUCATION_DATA[activeTab].color
                      }}>
                        <Shield size={12} />
                      </div>
                      <span style={{ fontSize: isMobile ? '0.85rem' : '0.95rem', fontWeight: 600, color: '#0f2d5e' }}>{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <div style={{ marginTop: isMobile ? '16px' : '24px', alignSelf: 'flex-start', position: 'relative' }}>
                  {(activeTab === 0 || activeTab === 3) && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '-12px', 
                      left: '12px', 
                      background: '#e65100', 
                      color: 'white', 
                      fontSize: '0.65rem', 
                      fontWeight: 800, 
                      padding: '2px 8px', 
                      borderRadius: '6px',
                      zIndex: 10,
                      letterSpacing: '0.05em',
                      boxShadow: '0 2px 8px rgba(230,81,0,0.4)'
                    }}>
                      ÇOK YAKINDA
                    </div>
                  )}
                  <div style={{ opacity: (activeTab === 0 || activeTab === 3) ? 0.5 : 1, cursor: (activeTab === 0 || activeTab === 3) ? 'not-allowed' : 'pointer', paddingTop: (activeTab === 0 || activeTab === 3) ? '8px' : '0' }}>
                    <div style={{ pointerEvents: (activeTab === 0 || activeTab === 3) ? 'none' : 'auto' }}>
                      <GlassButton
                        size={isMobile ? "sm" : "default"}
                        contentClassName="group"
                        onClick={(activeTab === 0 || activeTab === 3) ? undefined : () => {
                          if (activeTab === 2) {
                            window.open('https://forms.gle/FbT5YpVczWhcX4FMA', '_blank');
                          } else if (activeTab === 1) {
                            window.open('https://forms.gle/NC6esESKfjgFmE6c6', '_blank');
                          } else {
                            navigate('/iletisim');
                          }
                        }}
                      >
                        BAŞVUR <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                      </GlassButton>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────

const JoinUs: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    document.title = "Flying Slap - Nasıl Katılabilirsiniz?";
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="font-body bg-white">

      {/* ── HERO ────────────────────────────────────── */}
      <section
        className="relative w-full pt-32 pb-8 md:pt-28 md:pb-12 flex items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(160deg, rgba(14,165,233,0.04) 0%, rgba(15,45,94,0.02) 60%, white 100%)' }}
      >
        <div style={{ position: 'absolute', top: '-10%', left: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(230,81,0,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="relative z-10 flex flex-col items-center text-center px-6 w-full max-w-5xl"
          style={{ marginTop: isMobile ? '4vh' : '0' }}
        >
          <span style={{ display: 'block', color: '#0f2d5e', fontSize: '0.75rem', letterSpacing: '0.35em', marginBottom: 16, fontWeight: 800 }}>
            ADIM ADIM
          </span>

          <h1
            className="font-black font-headline uppercase tracking-tighter leading-none"
            style={{ fontSize: isMobile ? '2.4rem' : '4.8rem', color: '#0f2d5e' }}
          >
            NASIL{' '}
            <span style={{ color: '#e65100' }}>KATILABİLİRSİNİZ?</span>
          </h1>

          <div style={{ color: '#0f2d5e', fontSize: isMobile ? '15px' : '1.1rem', maxWidth: 800, lineHeight: '1.8', fontWeight: 500, marginTop: 28 }}>
            <p>
              Drone futbolu; fiziksel çevikliğin strateji ve teknolojiyle buluştuğu yenilikçi bir spor branşıdır. Takım ruhunu, teknoloji okuryazarlığını ve anlık karar verme becerilerini geliştirirken her yaş grubuna eşsiz bir deneyim sunar.
            </p>
            <p style={{ marginTop: '1rem' }}>
              Kulübümüz çatısı altında lisanslı sporcu olmak ve ulusal/uluslararası turnuvalarda yer almak için aşağıdaki kayıt adımlarını izlemeniz ve yaş grubunuza uygun başvuru formunu doldurmanız yeterlidir.
            </p>
            <span style={{ display: 'block', marginTop: 24, color: '#e65100', fontWeight: 900, fontSize: isMobile ? '1.1rem' : '1.2rem', letterSpacing: '0.05em' }}>
              Maceraya Başlamak İçin Hazır Olun!
            </span>
          </div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            style={{ marginTop: 40, width: 1, height: 56, background: 'linear-gradient(to bottom, #e65100, transparent)' }}
          />
        </motion.div>
      </section>

      {/* ── STEPS ───────────────────────────────────── */}
      <section className="relative w-full pt-4 pb-14 lg:pt-8 lg:pb-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">



          {/* Zigzag steps with central line */}
          <div className="relative">
            {/* Vertical center line (desktop only) */}
            <div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2"
              style={{ width: 1, background: 'linear-gradient(to bottom, transparent, #e5e7eb 8%, #e5e7eb 92%, transparent)' }}
            />

            <ZigzagSteps isMobile={isMobile} />
          </div>

        </div>
      </section>

      {/* ── EDUCATION SECTION ────────────────────────── */}
      <EducationSection />

      {/* ── HAZIR MISINIZ CTA ────────────────────────── */}
      <section id="hazir-misin" className="relative w-full overflow-hidden border-t border-black/5 flex items-center" style={{ minHeight: isMobile ? 140 : 160 }}>
        <ShaderAnimation />
        <div className={`relative z-10 max-w-7xl mx-auto px-8 md:px-16 w-full`} style={{ paddingTop: isMobile ? '48px' : '64px', paddingBottom: isMobile ? '48px' : '64px' }}>
          <div className={`flex flex-col md:flex-row items-center justify-between gap-8 ${isMobile ? 'text-center' : 'text-left'}`}>
            <div className={`flex flex-col gap-2 ${isMobile ? 'items-center text-center' : 'items-start text-left'}`}>
              <h3
                className={`font-black font-headline tracking-tighter leading-[0.88] ${isMobile ? '' : 'text-3xl md:text-[40px]'}`}
                style={{ fontSize: isMobile ? '28px' : undefined }}
              >
                <span style={{ color: '#e65100' }}>HAZIR</span>{' '}
                <span style={{ color: '#0f2d5e' }}>MISINIZ?</span>
              </h3>
              <p className={`font-body ${isMobile ? 'text-xs' : 'text-sm'}`} style={{ color: '#0f2d5e', fontWeight: 600 }}>
                Geleceğin sporuna adım atmak ve aramıza katılmak için hemen iletişime geçin.
              </p>
            </div>
            <div className={isMobile ? 'scale-90 origin-center' : ''}>
              <GlassButton size="default" contentClassName="group" onClick={() => navigate('/iletisim')}>
                BİZE KATILIN <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </GlassButton>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default JoinUs;
